import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { animalKeys } from '../queries/keys';
import { animalPaths } from '../queries/paths';

// TSS vite/03 — server-state hooks for animals (api-reference/admin-animals.md).
// Reads embed `race`. Only the list is surfaced in the UI for now; the rest
// are ready for when the Animals CRUD lands.

// Página por defecto del listado (contrato: limit def 50 / máx 500).
const LIST_LIMIT = 50;

export function useAnimalsList(filters) {
    return useQuery({
        queryKey: animalKeys.list(filters),
        queryFn:  ({ signal }) => api.get(animalPaths.list(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
    });
}

// Listado paginado "cargar más" (offset). Las páginas acumuladas viven en TanStack
// Query — vite/03 [RIGID], nunca en useState. getNextPageParam corta cuando offset+limit
// alcanza el total del backend.
export function useAnimalsInfinite(filters, limit = LIST_LIMIT) {
    return useInfiniteQuery({
        queryKey: animalKeys.infinite(filters, limit),
        queryFn:  ({ pageParam, signal }) =>
            api.get(animalPaths.list(), { params: { ...filters, limit, offset: pageParam }, signal }).then(r => r.data),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const p = last?.pagination;
            if (!p) return undefined;
            const next = p.offset + p.limit;
            return next < p.total ? next : undefined;
        },
        placeholderData: (prev) => prev,
    });
}

export function useAnimal(id) {
    return useQuery({
        queryKey: animalKeys.detail(id),
        queryFn:  ({ signal }) => api.get(animalPaths.detail(id), { signal }).then(r => r.data),
        enabled:  !!id && id !== 'new',
    });
}

export function useCreateAnimal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(animalPaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: animalKeys.all }),
    });
}

export function useUpdateAnimal(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(animalPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: animalKeys.detail(id) });
            qc.invalidateQueries({ queryKey: animalKeys.all });
        },
    });
}

export function useDeleteAnimal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(animalPaths.detail(id)).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: animalKeys.all }),
    });
}
